<template>
  <div class="sea-calculator">
    <div class="calculator-header">
      <h3>
        <i class="bx bxs-ship"></i>
        Калькулятор морских перевозок
      </h3>
    </div>
    
    <div class="calculator-content">
      <!-- Форма ввода параметров -->
      <div class="calculator-form">
        <div class="form-row">
          <div class="form-group">
            <label>Вес, кг</label>
            <input 
              type="number" 
              v-model="formData.weight" 
              placeholder="Введите вес"
              @input="calculate"
            >
          </div>
          <div class="form-group">
            <label>
              Количество паллет
              <i 
                id="pallets-tooltip"
                class="bx bx-help-circle form-tooltip"
                v-b-tooltip.hover.top="'В случае если груз россыпью - укажите примерное количество паллет для более точного расчета'"
              ></i>
            </label>
            <input 
              type="number" 
              v-model="formData.pallets" 
              placeholder="Введите количество"
              @input="calculate"
              :min="0"
              :max="maxPallets"
            >
            <small class="form-hint" v-if="maxPallets">
              Максимум для расчета: {{ maxPallets }} паллет
            </small>
          </div>
        </div>
      </div>
      
      <!-- Результаты расчета -->
      <div class="calculation-results" v-if="calculationResult">
        <div class="results-header">
          <h4>Результат расчета</h4>
        </div>
        
        <div class="results-content">
          <div class="result-item">
            <span class="label">Вес:</span>
            <span class="value">{{ formData.weight }} кг</span>
          </div>
          <div class="result-item">
            <span class="label">Количество паллет:</span>
            <span class="value">{{ formData.pallets }} шт.</span>
          </div>
          <div class="result-item total">
            <span class="label">Стоимость:</span>
            <span class="value">€{{ calculationResult.totalCost.toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <!-- Информационные карточки -->
      <div class="info-cards">
        <h4>Особенности морских перевозок</h4>
        <div class="info-cards-grid">
          <div class="info-card">
            <div class="card-header">
              <div class="card-icon">
                <i class="bx bxs-ship"></i>
              </div>
              <h5>Расчет стоимости</h5>
              <span class="card-subtitle">По максимальному значению</span>
            </div>
            <div class="card-content">
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Стоимость рассчитывается по весу</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Учитывается количество паллет</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Выбирается максимальное значение</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Тарифная сетка по весу и паллетам</span>
              </div>
            </div>
          </div>

          <div class="info-card">
            <div class="card-header">
              <div class="card-icon">
                <i class="bx bx-time"></i>
              </div>
              <h5>Сроки доставки</h5>
              <span class="card-subtitle">Морские перевозки</span>
            </div>
            <div class="card-content">
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Транзитное время: 25-35 дней</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Консолидация: 3-7 дней</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Таможенное оформление: 1-3 дня</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Доставка до места: 1-2 дня</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { API_CALCULATOR_SETTINGS_SEA } from '@/constants'

export default {
  name: 'SeaCalculator',
  
  data() {
    return {
      isLoading: false,
      tariffs: [],
      formData: {
        weight: '',
        pallets: ''
      },
      calculationResult: null
    }
  },
  
  computed: {
    maxPallets() {
      if (this.tariffs.length === 0) return null
      return Math.max(...this.tariffs.map(tariff => tariff.pll))
    }
  },
  
  async mounted() {
    await this.loadTariffs()
  },
  
  methods: {
    async loadTariffs() {
      try {
        this.isLoading = true
        const response = await axios.get(API_CALCULATOR_SETTINGS_SEA)
        
        if (response.data.success && response.data.data) {
          this.tariffs = response.data.data.tariffs || []
        }
      } catch (error) {
        console.error('Ошибка загрузки тарифов:', error)
        this.$toast.error('Ошибка загрузки тарифов')
      } finally {
        this.isLoading = false
      }
    },
    
    calculate() {
      if (!this.formData.weight || !this.formData.pallets) {
        this.calculationResult = null
        return
      }
      
      const weight = parseFloat(this.formData.weight)
      const pallets = parseInt(this.formData.pallets)
      
      // Валидация максимального количества паллет
      if (this.maxPallets && pallets > this.maxPallets) {
        this.$toast.warning(`Максимальное количество паллет: ${this.maxPallets}`)
        return
      }
      
      // Расчет только по весу и паллетам
      const costByWeight = this.findCostByWeight(weight, pallets)
      
      this.calculationResult = {
        totalCost: costByWeight
      }
    },
    
    
    findCostByWeight(weight, pallets) {
      // Сортируем тарифы по весу для поиска
      const sortedTariffs = [...this.tariffs].sort((a, b) => a.weight_kg - b.weight_kg)
      
      // Ищем подходящий тариф по весу (ВПР с ИСТИНА - приблизительный поиск)
      let weightTariff = null
      for (let i = sortedTariffs.length - 1; i >= 0; i--) {
        if (weight >= sortedTariffs[i].weight_kg) {
          weightTariff = sortedTariffs[i]
          break
        }
      }
      
      // Если не найден тариф по весу, используем минимальный
      if (!weightTariff) {
        weightTariff = sortedTariffs[0]
      }
      
      // Теперь ищем тариф по количеству паллет
      const palletTariff = this.tariffs.find(tariff => tariff.pll === pallets)
      
      // Если найден тариф по паллетам, берем максимальную стоимость между весом и паллетами
      if (palletTariff) {
        return Math.max(weightTariff.cost_eu, palletTariff.cost_eu)
      }
      
      // Если тариф по паллетам не найден, возвращаем стоимость по весу
      return weightTariff.cost_eu
    }
  }
}
</script>

<style lang="scss" scoped>
.sea-calculator {
  .calculator-header {
    margin-bottom: 20px;
    
    h3 {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      color: #0d6348;
      font-size: 18px;
      
      i {
        font-size: 20px;
      }
    }
  }
  
  .calculator-content {
    .calculator-form {
      background: #fff;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      
      .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 15px;
        margin-bottom: 15px;
        
        &.three-columns {
          grid-template-columns: 1fr 1fr 1fr;
          
          @media (max-width: 1200px) {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        @media (max-width: 768px) {
          grid-template-columns: 1fr;
          
          &.three-columns {
            grid-template-columns: 1fr;
          }
        }
      }
      
      .form-group {
        display: flex;
        flex-direction: column;
        
        label {
          font-size: 12px;
          font-weight: 500;
          color: #333;
          margin-bottom: 5px;
          display: flex;
          align-items: center;
          gap: 4px;
          
          .form-tooltip {
            color: #0d6348;
            cursor: help;
            font-size: 14px;
            transition: color 0.2s;
            
            &:hover {
              color: #0a4d3a;
            }
          }
        }
        
        input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          transition: border-color 0.3s;
          
          &:focus {
            outline: none;
            border-color: #0d6348;
          }
        }
        
        .form-hint {
          display: block;
          margin-top: 4px;
          font-size: 11px;
          color: #666;
          font-style: italic;
        }
      }
    }
    
    .calculation-results {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;
      border: 1px solid #e9ecef;
      
      .results-header {
        margin-bottom: 15px;
        
        h4 {
          margin: 0;
          color: #0d6348;
          font-size: 16px;
        }
      }
      
      .results-content {
        .result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
          
          &.total {
            border-bottom: none;
            font-weight: 600;
            font-size: 16px;
            color: #0d6348;
            margin-top: 10px;
            padding-top: 15px;
            border-top: 2px solid #0d6348;
          }
          
          .label {
            color: #666;
            font-size: 14px;
          }
          
          .value {
            font-weight: 500;
            color: #333;
          }
        }
      }
    }
    
    .info-cards {
      margin-top: 30px;
      
      h4 {
        margin-bottom: 20px;
        color: #0d6348;
        font-size: 18px;
        font-weight: 600;
      }
      
      .info-cards-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        margin-bottom: 30px;
        
        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }
      }
      
      .info-card {
        background: #fff;
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border: 1px solid #e9ecef;
        transition: all 0.3s ease;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
        
        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          
          .card-icon {
            width: 50px;
            height: 50px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            background: linear-gradient(135deg, #0d6348, #1a7a5e);
            
            i {
              font-size: 24px;
            }
          }
          
          h5 {
            margin: 0;
            color: #0d6348;
            font-size: 16px;
            font-weight: 600;
          }
          
          .card-subtitle {
            color: #666;
            font-size: 12px;
            font-weight: 500;
          }
        }
        
        .card-content {
          .info-item {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
            font-size: 14px;
            color: #333;
            
            i {
              color: #28a745;
              font-size: 16px;
            }
            
            span {
              line-height: 1.4;
            }
          }
        }
      }
      
    }
  }
}
</style>